import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ACTIONS, PRODUCTTYPE } from "@enums/index.enum";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { SascMenuModel } from "@shared/interfaces/user.model";
import { map } from "rxjs/operators";
import { NotificationService } from "src/app/shared/services/notification/notification.service";
import { environment } from "src/environments/environment";

import {
  CLEAR_DASHBOARD_TAB,
  DOWNLOAD_DOCUMENT,
  DOWNLOAD_MOTOR_APPROVAL_REPORT,
  GET_ACTIVE_AGENTS_LIST,
  GET_AGENT_LIST,
  GET_AGENT_LIST_BY_BRANCH,
  GET_BRANCH_LIST,
  GET_CHILDSUBMENU_REDIRECTURL,
  GET_DEFAULT_LIST,
  GET_DEFAULT_MT_APPROVAL_LIST,
  GET_DOCUMENT_LOV,
  GET_DOCUMENTS,
  GET_DRAFT_SEARCH_LIST,
  GET_EQUOTE_PRODUCT_ACCESS,
  GET_EXPIRING_SEARCH_LIST,
  GET_LOV,
  GET_MARKETER_MANAGER,
  GET_MOTOR_APPROVAL_SEARCH_LIST,
  GET_PENDING_APPROVAL_SEARCH_LIST,
  GET_POLICY_ENQUIRY_LIST,
  GET_REDIRECT_PAGE,
  GET_REFERRED_LIST,
  GET_REQUOTE_DOCUMENTS,
  GET_SUBMENU_REDIRECTURL,
  GET_SUMMARY_SEARCH_LIST,
  GET_UNIFIED_LOV,
  GET_UNIFIED_LOV_IN_SUMMARY_DASHBOARD,
  MAIN_PRODUCT_TYPE,
  PAGER_SELECTION_SETTER,
  PROMPT_ERROR,
  REDIRECT_TO_SAT_MOTOR,
  RESET_DASHBOARD_SEARCH_RESULT_LIST,
  RESET_MARKETER_AGENT_MANAGER,
  RESET_PROMPT_ERROR,
  SET_ACITVE_SUMMARY,
  SET_DRAFT_FILTER_CRITERIA,
  SET_EXPIRING_FILTER_CRITERIA,
  SET_MOTOR_APPROVAL_FILTER_CRITERIA,
  SET_PENDING_APPROVAL_FILTER_CRITERIA,
  SET_PRODUCT,
  SET_SUMMARY_FILTER_CRITERIA,
  STORE_DASHBOARD_TAB,
} from "./dashboard.action";
import {
  AgentList,
  BranchList,
  DASHBOARD_DefaultParams,
  DASHBOARD_DraftItem,
  DASHBOARD_ExpiringItem,
  DASHBOARD_LOV,
  DASHBOARD_MotorApprovalItem,
  DASHBOARD_PagerSetting,
  DASHBOARD_PendingApprovalItem,
  DASHBOARD_SummaryItem,
  DASHBOARD_SummaryMTItem,
  LOV_LIST_OF_VALUE,
  LOV_RESPONSE,
} from "./dashboard.interface";
import { DashboardService } from "./dashboard.service";

type DashboardDefaultListPatch = Partial<
  Pick<
    DashboardModel,
    | "summaryList"
    | "summaryCount"
    | "summaryMTList"
    | "summaryMTCount"
    | "draftList"
    | "draftCount"
    | "expiringList"
    | "expiringCount"
    | "pendingApprovalList"
    | "pendingApprovalCount"
  >
>;

interface DashboardModel {
  summaryList: Array<DASHBOARD_SummaryItem<number>>;
  draftList: Array<DASHBOARD_DraftItem<number>>;
  expiringList: Array<DASHBOARD_ExpiringItem<number>>;
  pendingApprovalList: Array<DASHBOARD_PendingApprovalItem<number>>;
  motorApprovalList: Array<DASHBOARD_MotorApprovalItem<number>>;
  branchList: Array<BranchList>;
  agentList: Array<AgentList>;
  referredList: any;
  marketingManagerList: any;
  pendingApprovalCount: number;
  summaryCount: number;
  expiringCount: number;
  draftCount: number;
  motorCount: number;
  product: string;
  cartDocumentDetails: any;
  lov: Partial<DASHBOARD_LOV>;
  LOV: LOV_LIST_OF_VALUE | null;
  menu: SascMenuModel[] | null;
  policyEnquiryList: Array<any>;
  pagerSetting: DASHBOARD_PagerSetting | null;
  summaryFilterCriteria;
  draftFilterCriteria;
  expiringSoonFilterCriteria;
  pendingApprovalFilterCriteria;
  motorApprovalFilterCriteria;
  mainProductType: any;
  promptError: {
    isShow: boolean;
    msg: string;
  };
  dashboardTab: any;
  equoteProductAccess: boolean;
  activeAgentsList: any;
  activeSummaryTab: string;
  motorSummaryFilterCriteria;
  summaryMTList: Array<DASHBOARD_SummaryMTItem<number>>;
  summaryMTCount: number;
}

@State<DashboardModel>({
  name: "DashboardState",
  defaults: {
    summaryList: [],
    draftList: [],
    expiringList: [],
    pendingApprovalList: [],
    motorApprovalList: [],
    pendingApprovalCount: null,
    summaryCount: null,
    expiringCount: null,
    draftCount: null,
    motorCount: null,
    product: null,
    lov: null,
    cartDocumentDetails: null,
    LOV: null,
    menu: null,
    policyEnquiryList: null,
    pagerSetting: null,
    summaryFilterCriteria: null,
    motorSummaryFilterCriteria: null,
    draftFilterCriteria: null,
    expiringSoonFilterCriteria: null,
    pendingApprovalFilterCriteria: null,
    motorApprovalFilterCriteria: null,
    mainProductType: null,
    promptError: {
      isShow: false,
      msg: null,
    },
    dashboardTab: null,
    equoteProductAccess: false,
    branchList: [],
    agentList: [],
    referredList: [],
    marketingManagerList: [],
    activeAgentsList: [],
    activeSummaryTab: null,
    summaryMTList: [],
    summaryMTCount: null,
  },
})
@Injectable()
export class DashboardState {
  constructor(
    private readonly dashboardService: DashboardService,
    private readonly notificationService: NotificationService,
    private readonly store: Store,
    private readonly crossAppNavigationService: CrossAppNavigationService,
  ) {}

  private static getDocumentLov(state: DashboardModel) {
    return state.LOV?.documentList;
  }

  private getCombinedDocumentLovList(
    state: DashboardModel,
    productCode: string,
  ): Array<any> {
    const documentLov = DashboardState.getDocumentLov(state);
    const mandatoryDocumentList =
      documentLov?.mandatoryViewDocumentList?.[`${productCode}`] ?? [];
    const optionalDocumentList =
      documentLov?.optionalViewDocumentList?.[`${productCode}`] ?? [];

    return optionalDocumentList
      .concat(mandatoryDocumentList)
      .filter((data) => data != undefined);
  }

  @Selector()
  public static product(state: DashboardModel) {
    return state.product ? state.product : [];
  }

  @Selector()
  public static summaryList(state: DashboardModel) {
    return state.summaryList ? state.summaryList : [];
  }

  @Selector()
  public static prodTypeList(state: DashboardModel) {
    return state.LOV?.prodType ?? [];
  }

  @Selector()
  public static enquiryProdList(state: DashboardModel) {
    return state.LOV?.enquiryProdType ?? [];
  }

  @Selector()
  public static deductibleCodeList(state: DashboardModel) {
    return state.LOV?.deductibleCodeList ?? [];
  }

  @Selector()
  public static summaryCount(state: DashboardModel) {
    return state.summaryCount ? state.summaryCount : 0;
  }

  @Selector()
  public static draftList(state: DashboardModel) {
    return state.draftList ? state.draftList : [];
  }

  @Selector()
  public static draftCount(state: DashboardModel) {
    return state.draftCount ? state.draftCount : 0;
  }

  @Selector()
  public static expiringList(state: DashboardModel) {
    return state.expiringList ? state.expiringList : [];
  }

  @Selector()
  public static expiringCount(state: DashboardModel) {
    return state.expiringCount ? state.expiringCount : 0;
  }

  @Selector()
  public static pendingApprovalList(state: DashboardModel) {
    return state.pendingApprovalList ? state.pendingApprovalList : [];
  }

  @Selector()
  public static pendingApprovalCount(state: DashboardModel) {
    return state.pendingApprovalCount ? state.pendingApprovalCount : 0;
  }

  @Selector()
  public static motorApprovalList(state: DashboardModel) {
    return state.motorApprovalList ? state.motorApprovalList : [];
  }

  @Selector()
  public static motorCount(state: DashboardModel) {
    return state.motorCount ? state.motorCount : 0;
  }

  @Selector()
  public static pdpaMessage(state: DashboardModel) {
    return state.LOV?.pdpaMessage ?? null;
  }

  @Selector()
  public static policyCriteria(state: DashboardModel) {
    return state.LOV ? state.LOV.policyCriteria : [];
  }

  @Selector()
  public static clientTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientTypeList : [];
  }

  @Selector()
  public static cnTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.ecnTypesList : [];
  }

  @Selector()
  public static cnStatus(state: DashboardModel) {
    return state.LOV ? state.LOV.cnStatus : [];
  }

  @Selector()
  public static cnCriteria(state: DashboardModel) {
    return state.LOV ? state.LOV.cnCriteria : [];
  }

  @Selector()
  public static proposalStatus(state: DashboardModel) {
    return state.LOV ? state.LOV.proposalStatus : [];
  }

  @Selector()
  public static proposalCriteria(state: DashboardModel) {
    return state.LOV ? state.LOV.proposalCriteria : [];
  }

  @Selector()
  public static productList(state: DashboardModel) {
    return state.LOV ? state.LOV.productList : [];
  }

  @Selector()
  public static clientFax1CodeList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientFax1CodeList : [];
  }

  @Selector()
  public static clientMobileCode1List(state: DashboardModel) {
    return state.LOV ? state.LOV.clientMobileCode1List : [];
  }

  @Selector()
  public static clientPhoneCodeList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientPhoneCodeList : [];
  }

  @Selector()
  public static postcodeList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientPostcodeList : [];
  }

  @Selector()
  public static cityList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientCityList : [];
  }

  @Selector()
  public static stateList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientStateList : [];
  }

  @Selector()
  public static countryList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientCountryList : [];
  }

  @Selector()
  public static nationalityList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientNationalityList : [];
  }

  @Selector()
  public static constructionRoofList(state: DashboardModel) {
    return state.LOV ? state.LOV.constructionRoofList : [];
  }

  @Selector()
  public static constructionWallList(state: DashboardModel) {
    return state.LOV ? state.LOV.constructionWallList : [];
  }

  @Selector()
  public static issueProductList(state: DashboardModel) {
    return state.LOV ? state.LOV.productList : [];
  }

  @Selector()
  public static constructionYearComboList(state: DashboardModel) {
    return state.LOV ? state.LOV.constructionYearComboList : [];
  }

  @Selector()
  public static fireSubOccupTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.fireSubOccupTypeList : [];
  }

  @Selector()
  public static typeOfGoodsList(state: DashboardModel) {
    return state.LOV ? state.LOV.typeOfGoodsList : [];
  }

  @Selector()
  public static indemnityPeriodTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.indemnityPeriodTypeList : [];
  }

  @Selector()
  public static timeExcessTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.timeExcessTypeList : [];
  }

  @Selector()
  public static typeOfIndustryList(state: DashboardModel) {
    return state.LOV ? state.LOV.typeOfIndustryList : [];
  }

  @Selector()
  public static combinedNameType1List(state: DashboardModel) {
    return state.LOV ? state.LOV.combinedNameType1List : [];
  }

  @Selector()
  public static combinedNameRoleTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.combinedNameRoleTypeList : [];
  }

  @Selector()
  public static mortgageeTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.mortgageeTypeList : [];
  }

  @Selector()
  public static noOfDaysList(state: DashboardModel) {
    return state.LOV ? state.LOV.noOfDaysList : [];
  }

  @Selector()
  public static sumInsuredPerDayList(state: DashboardModel) {
    return state.LOV ? state.LOV.sumInsuredPerDayList : [];
  }

  @Selector()
  public static occupierTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.occupierTypeList : [];
  }

  @Selector()
  public static indemnityLimitList(state: DashboardModel) {
    return state.LOV ? state.LOV.indemnityLimitList : [];
  }

  @Selector()
  public static rebateTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.rebateTypeList : [];
  }

  @Selector()
  public static satLinkRedirectInd(state: DashboardModel) {
    return state.LOV ? state.LOV.satLinkRedirectInd : false;
  }

  @Selector()
  public static lov(state: DashboardModel): LOV_LIST_OF_VALUE | null {
    return state.LOV;
  }

  @Selector()
  public static declarationByAgent(state: DashboardModel) {
    return state.LOV?.declarationByAgent ?? null;
  }
  @Selector()
  public static sedarimportantnote(state: DashboardModel) {
    return state.LOV?.importantNote ?? null;
  }

  @Selector()
  public static paymentModeList(state: DashboardModel) {
    return state.LOV ? state.LOV.paymentModeList : [];
  }

  @Selector()
  public static paymentMadeTo(state: DashboardModel) {
    return state.LOV ? state.LOV.paymentModeTo : [];
  }

  @Selector()
  public static bankNameList(state: DashboardModel) {
    return state.LOV ? state.LOV.bankNameList : [];
  }

  @Selector()
  public static cardTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.cardTypeList : [];
  }
  @Selector()
  public static cardRelationList(state: DashboardModel) {
    return state.LOV ? state.LOV.cardRelationList : [];
  }
  @Selector()
  public static cardExpiryMonthList(state: DashboardModel) {
    return state.LOV ? state.LOV.cardExpiryMonthList : [];
  }
  @Selector()
  public static policyEnquiryList(state: DashboardModel) {
    return state.policyEnquiryList ? state.policyEnquiryList : [];
  }
  @Selector()
  public static loadingTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.loadingTypeList : [];
  }

  @Selector()
  public static diagnosisList(state: DashboardModel) {
    return state.LOV ? state.LOV.diagnosisList : [];
  }

  @Selector()
  public static narrTypeList(state: DashboardModel) {
    return state.LOV ? state.LOV.narrTypeList : [];
  }

  // @Selector()
  // public static ccExpiryYearList(state: DashboardModel) {
  //     return state.expi ? state.ccExpiryYear : [];
  // }

  @Selector()
  public static summarySearchCriteria(state: DashboardModel) {
    return state.summaryFilterCriteria ? state.summaryFilterCriteria : null;
  }

  @Selector()
  public static draftSearchCriteria(state: DashboardModel) {
    return state.draftFilterCriteria ? state.draftFilterCriteria : null;
  }

  @Selector()
  public static expiringSearchCriteria(state: DashboardModel) {
    return state.expiringSoonFilterCriteria
      ? state.expiringSoonFilterCriteria
      : null;
  }

  @Selector()
  public static pendingApprovalFilterCriteria(state: DashboardModel) {
    return state.pendingApprovalFilterCriteria
      ? state.pendingApprovalFilterCriteria
      : null;
  }

  @Selector()
  public static motorApprovalFilterCriteria(state: DashboardModel) {
    return state.motorApprovalFilterCriteria
      ? state.motorApprovalFilterCriteria
      : null;
  }

  @Selector()
  public static promptError(state: DashboardModel) {
    return state.promptError ? state.promptError : null;
  }

  @Selector()
  public static clientOccupList(state: DashboardModel) {
    return state.LOV ? state.LOV.clientOccupList : [];
  }

  @Selector()
  public static clientBusinessList(state: DashboardModel) {
    return state.LOV ? state.LOV.businessCodeList : [];
  }

  @Selector()
  public static dependentRelationList(state: DashboardModel) {
    return state.LOV ? state.LOV.dependentRelationList : [];
  }

  @Selector()
  public static nomineeRelationList(state: DashboardModel) {
    return state.LOV ? state.LOV.nomineeRelationList : [];
  }

  @Selector()
  public static isEinvoiceConsentEnabled(state: DashboardModel) {
    return state.LOV ? state.LOV.isEinvoiceConsentEnabled : false;
  }

  @Selector()
  public static isEinvoiceCompanyIndEnabled(state: DashboardModel) {
    return state.LOV ? state.LOV.isEinvoiceCompanyIndEnabled : false;
  }

  @Selector()
  public static fireRetailSubOccupTypeList(state: DashboardModel) {
    if (state.LOV) {
      const retailSubOccupList = state.LOV.subOccupRetail;
      return retailSubOccupList;
    }
    return state.LOV ? state.LOV.fireSubOccupTypeList : [];
  }

  @Action(GET_LOV)
  public getDefaultList(
    { patchState }: StateContext<DashboardModel>,
    { productCode, userId }: GET_LOV,
  ) {
    return this.dashboardService.getLOV(productCode, userId).pipe(
      map((res: LOV_RESPONSE) => {
        patchState({
          LOV: res ? res.data : null,
        });
      }),
    );
  }

  @Selector()
  public static branchList(state: DashboardModel) {
    return state.branchList ? state.branchList : [];
  }

  @Selector()
  public static agentList(state: DashboardModel) {
    return state.agentList ? state.agentList : [];
  }

  @Selector()
  public static referredList(state: DashboardModel) {
    return state.referredList ? state.referredList : [];
  }

  @Selector()
  public static marketingManagerList(state: DashboardModel) {
    return state.marketingManagerList ? state.marketingManagerList : [];
  }

  @Selector()
  public static marketingManagerTableData(state: DashboardModel) {
    return state.marketingManagerList?.data?.agentList ?? [];
  }

  @Selector()
  public static activeAgentsList(state: DashboardModel) {
    return state.activeAgentsList ? state.activeAgentsList : [];
  }

  @Selector()
  public static activeAgentsTableData(state: DashboardModel) {
    return state.activeAgentsList?.data?.agentList ?? [];
  }

  @Action(GET_BRANCH_LIST)
  public getBranchList(
    { patchState }: StateContext<DashboardModel>,
    { userId }: GET_BRANCH_LIST,
  ) {
    return this.dashboardService.getBranchList(userId).pipe(
      map((res) => {
        patchState({
          branchList: res?.data ? res.data?.userBranchList : [],
        });
      }),
    );
  }

  @Action(GET_AGENT_LIST)
  public getAgentList(
    { patchState }: StateContext<DashboardModel>,
    { userId }: GET_AGENT_LIST,
  ) {
    return this.dashboardService.getAgentList(userId).pipe(
      map((res) => {
        patchState({
          agentList: res?.data ? res.data?.agentList : [],
        });
      }),
    );
  }

  @Action(GET_AGENT_LIST_BY_BRANCH)
  public getAgentListByBranch(
    { patchState }: StateContext<DashboardModel>,
    { userId, branchCode }: GET_AGENT_LIST_BY_BRANCH,
  ) {
    return this.dashboardService.getAgentListByBranch(userId, branchCode).pipe(
      map((res) => {
        patchState({
          agentList: res?.data ? res.data?.agentList : [],
        });
      }),
    );
  }

  @Action(GET_REFERRED_LIST)
  public getReferredList({ patchState }: StateContext<DashboardModel>) {
    return this.dashboardService.getReferredList().pipe(
      map((res) => {
        patchState({
          referredList: this.filterReferredList(res) || [],
        });
      }),
    );
  }

  public filterReferredList(res: any): any {
    if (!res || !Array.isArray(res)) {
      return [];
    }

    return res.map((item: any) => {
      return {
        code: item.cmgcCodeDetPk?.code,
        descp: item.description,
      };
    });
  }

  @Action(GET_MARKETER_MANAGER)
  public getMarketerManager(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_MARKETER_MANAGER,
  ) {
    return this.dashboardService.getMarketerManager(payload).pipe(
      map((res) => {
        patchState({
          marketingManagerList: res ? res : [],
        });
      }),
    );
  }

  @Action(RESET_MARKETER_AGENT_MANAGER)
  public resetMarketerManager({ patchState }: StateContext<DashboardModel>) {
    patchState({
      marketingManagerList: [],
      activeAgentsList: [],
    });
  }

  @Selector()
  public static enhancementCutOffDate(state: DashboardModel) {
    return DashboardState.getDocumentLov(state)?.enhancementCutOffDate ?? [];
  }

  @Selector()
  public static mandatoryViewDocumentList(state: DashboardModel) {
    return (
      DashboardState.getDocumentLov(state)?.mandatoryViewDocumentList ?? []
    );
  }

  @Selector()
  public static mandatoryUploadDocumentList(state: DashboardModel) {
    return (
      DashboardState.getDocumentLov(state)?.mandatoryUploadDocumentList ?? []
    );
  }

  @Selector()
  public static cartDocumentDetails(state: DashboardModel) {
    return state.cartDocumentDetails ? state.cartDocumentDetails : [];
  }

  @Selector()
  public static optionalViewDocumentList(state: DashboardModel) {
    return DashboardState.getDocumentLov(state)?.optionalViewDocumentList ?? [];
  }

  @Selector()
  public static optionalUploadDocumentList(state: DashboardModel) {
    return (
      DashboardState.getDocumentLov(state)?.optionalUploadDocumentList ?? []
    );
  }

  @Selector()
  public static smeCategoryList(state: DashboardModel) {
    return state.LOV ? state.LOV.smeCategoryList : [];
  }

  @Selector()
  public static accuPerilList(state: DashboardModel) {
    return state.LOV ? state.LOV.riskAccuReportPerilList : [];
  }

  @Selector()
  public static accuStatus(state: DashboardModel) {
    return state.LOV ? state.LOV.riskAccuReportStatus : [];
  }

  @Selector()
  public static einvoiceCriteria(state: DashboardModel) {
    return state.LOV ? state.LOV.einvoiceCriteria : [];
  }

  @Selector()
  public static einvoiceStatus(state: DashboardModel) {
    return state.LOV ? state.LOV.einvoiceStatus : [];
  }

  @Selector()
  public static lowestLevelOccupancyList(state: DashboardModel) {
    return state.LOV ? state.LOV.lowestLevelOccupancyList : [];
  }

  @Selector()
  public static basementLevelList(state: DashboardModel) {
    return state.LOV ? state.LOV.basementLevelList : [];
  }

  @Selector()
  public static isEmarketingConsentEnabled(state: DashboardModel) {
    return state.LOV ? state.LOV.isEmarketingConsentEnabled : false;
  }

  @Action(GET_UNIFIED_LOV)
  public getUnifiedLOV(
    { patchState }: StateContext<DashboardModel>,
    { productCode: _productCode, userId }: GET_LOV,
  ) {
    return this.dashboardService.getUnifiedLOV(userId).pipe(
      map((res: LOV_RESPONSE) => {
        patchState({
          LOV: res ? res.data : null,
        });
      }),
    );
  }

  @Action(GET_DEFAULT_MT_APPROVAL_LIST)
  public getDefaultMTApprovalList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_DEFAULT_MT_APPROVAL_LIST,
  ) {
    return this.dashboardService.fetchDefaultList(payload).pipe(
      map((res: any) => {
        if (res != null) {
          if (res.data) {
            if (res.data.motorApprovalCount > 0) {
              patchState({
                summaryList: [],
                draftList: [],
                expiringList: [],
                pendingApprovalList: [],
                motorApprovalList: res.data.motorApprovalList,
                motorCount: res.data.motorApprovalCount,
                pendingApprovalCount: 0,
                expiringCount: 0,
                summaryCount: 0,
                draftCount: 0,
              });
            } else if (res.data.motorApprovalCount == 0) {
              patchState({
                summaryList: [],
                draftList: [],
                expiringList: [],
                pendingApprovalList: [],
                motorApprovalList: [],
                pendingApprovalCount: 0,
                expiringCount: 0,
                summaryCount: 0,
                draftCount: 0,
                motorCount: 0,
              });
            }
          }
        } else {
          patchState({
            summaryList: [],
            draftList: [],
            expiringList: [],
            pendingApprovalList: [],
            motorApprovalList: [],
            pendingApprovalCount: 0,
            expiringCount: 0,
            summaryCount: 0,
            draftCount: 0,
            motorCount: 0,
          });
        }
      }),
    );
  }

  @Action(GET_DEFAULT_LIST)
  public getDefaultListx(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_DEFAULT_LIST,
  ) {
    if (!payload.clearState) {
      return this.dashboardService.fetchDefaultList(payload).pipe(
        map((res: any) => {
          patchState(this.buildDefaultListPatch(payload, res?.data));
        }),
      );
    } else {
      patchState(this.buildDefaultListPatch(payload, null));
    }
  }

  @Action(GET_SUMMARY_SEARCH_LIST)
  public getSummarySearchList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_SUMMARY_SEARCH_LIST,
  ) {
    return this.dashboardService.searchList(payload).pipe(
      map((res: any = {}) => {
        if (res.data) {
          if (payload.productType != "MT") {
            patchState({
              summaryList: res.data.dashboardCngeNoteList,
              summaryCount: res.data.cngeNoteCount,
            });
          }
          if (payload.productType == "MT") {
            patchState({
              summaryMTList:
                res.data.dashboardCngeNoteMTList ??
                res.data.dashboardCngeNoteList ??
                [],
              summaryMTCount:
                res.data.cngeNoteMTCount ?? res.data.cngeNoteCount ?? 0,
            });
          }
        } else {
          if (payload.productType != "MT") {
            patchState({
              summaryList: [],
              summaryCount: 0,
            });
          }
          if (payload.productType == "MT") {
            patchState({
              summaryMTList: [],
              summaryMTCount: 0,
            });
          }
        }
      }),
    );
  }

  @Action(GET_DRAFT_SEARCH_LIST)
  public getDraftSearchList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_DRAFT_SEARCH_LIST,
  ) {
    return this.dashboardService.searchList(payload).pipe(
      map((res: any = {}) => {
        if (res?.data?.dashboardCngeProposalNoteList) {
          patchState({
            draftList: res.data.dashboardCngeProposalNoteList,
            draftCount: res.data.cngeProposalCount,
          });
        } else {
          patchState({
            draftList: [],
            draftCount: 0,
          });
        }
      }),
    );
  }

  @Action(GET_EXPIRING_SEARCH_LIST)
  public getExpiringSearchList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_EXPIRING_SEARCH_LIST,
  ) {
    return this.dashboardService.searchList(payload).pipe(
      map((res: any) => {
        if (res.data) {
          patchState({
            expiringList: res?.data?.dashboardCngeExpiredProposalList,
            expiringCount: res.data.cngeExpiredProposalCount,
          });
        } else {
          patchState({
            expiringList: [],
            expiringCount: 0,
          });
        }
      }),
    );
  }

  @Action(GET_PENDING_APPROVAL_SEARCH_LIST)
  public getPendingApprovalSearchList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_PENDING_APPROVAL_SEARCH_LIST,
  ) {
    return this.dashboardService.searchList(payload).pipe(
      map((res: any) => {
        if (res.data) {
          patchState({
            pendingApprovalList: res?.data?.dashboardApprovalNoteList,
            pendingApprovalCount: res.data.cngeApprovalCount,
          });
        } else {
          patchState({
            pendingApprovalList: [],
            pendingApprovalCount: 0,
          });
        }
      }),
    );
  }

  @Action(GET_MOTOR_APPROVAL_SEARCH_LIST)
  public getMotorSearchList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_MOTOR_APPROVAL_SEARCH_LIST,
  ) {
    return this.dashboardService.searchList(payload).pipe(
      map((res: any) => {
        if (res.data?.motorApprovalCount > 0) {
          patchState({
            motorApprovalList: res?.data?.motorList?.motorApprovalList,
            motorCount: res.data.motorApprovalCount,
          });
        } else {
          patchState({
            motorApprovalList: [],
            motorCount: 0,
          });
        }
      }),
    );
  }

  @Action(SET_SUMMARY_FILTER_CRITERIA)
  public setSummaryFilterCriteria(
    { patchState }: StateContext<DashboardModel>,
    { payload },
  ) {
    if (payload == null) {
      patchState({
        summaryFilterCriteria: payload,
        motorSummaryFilterCriteria: payload,
      });
    } else if (payload && payload?.productType == "MT") {
      patchState({
        motorSummaryFilterCriteria: payload,
      });
    } else if (payload && payload?.productType != "MT") {
      patchState({
        summaryFilterCriteria: payload,
      });
    }
  }

  @Action(SET_DRAFT_FILTER_CRITERIA)
  public setDraftFilterCriteria(
    { patchState }: StateContext<DashboardModel>,
    { payload },
  ) {
    patchState({
      draftFilterCriteria: payload,
    });
  }

  @Action(SET_EXPIRING_FILTER_CRITERIA)
  public setExpiringFilterCriteria(
    { patchState }: StateContext<DashboardModel>,
    { payload },
  ) {
    patchState({
      expiringSoonFilterCriteria: payload,
    });
  }

  @Action(SET_PENDING_APPROVAL_FILTER_CRITERIA)
  public setPendingApprovalFilterCriteria(
    { patchState }: StateContext<DashboardModel>,
    { payload },
  ) {
    patchState({
      pendingApprovalFilterCriteria: payload,
    });
  }

  @Action(SET_MOTOR_APPROVAL_FILTER_CRITERIA)
  public setMotorApprovalFilterCriteria(
    { patchState }: StateContext<DashboardModel>,
    { payload },
  ) {
    patchState({
      motorApprovalFilterCriteria: payload,
    });
  }

  @Action(SET_PRODUCT)
  public setProductType(
    { patchState }: StateContext<DashboardModel>,
    { product }: SET_PRODUCT,
  ) {
    patchState({
      product: product,
    });
  }

  @Action(GET_SUBMENU_REDIRECTURL)
  public getSubMenuRedirectUrl(
    { patchState: _patchState }: StateContext<DashboardModel>,
    { payload }: GET_SUBMENU_REDIRECTURL,
  ) {
    if (environment.policyManagementDebugMode) {
      this.crossAppNavigationService.redirectToTrustedUrl(
        payload,
        "_self",
        "DashboardState.getSubMenuRedirectUrl.debug",
      );
    } else if (
      payload === "ISSUE" ||
      payload === "REPORTS" ||
      payload === "ENQUIRY" ||
      payload === "SUMMARY" ||
      payload === "CI"
    ) {
      const redirectPayload = {
        operator: this.store.selectSnapshot<string>(
          (state) => state.UserState.user.userId,
        ),
        action: payload,
      };
      this.store.dispatch(new GET_REDIRECT_PAGE(redirectPayload));
    } else {
      return this.dashboardService.getDirectUrlFromSubMenu(payload).pipe(
        map((res) => {
          if (res.redirectUrl) {
            this.crossAppNavigationService.redirectToTrustedUrl(
              res.redirectUrl,
              "_self",
              "DashboardState.getSubMenuRedirectUrl",
            );
          }
        }),
      );
    }
  }

  @Action(GET_CHILDSUBMENU_REDIRECTURL)
  public getChildSubMenuRedirectUrl(
    { patchState: _patchState }: StateContext<DashboardModel>,
    { payload }: GET_CHILDSUBMENU_REDIRECTURL,
  ) {
    const redirectPayload = {
      operator: this.store.selectSnapshot<string>(
        (state) => state.UserState.user.userId,
      ),
      action: payload,
    };
    this.store.dispatch(new GET_REDIRECT_PAGE(redirectPayload));
  }

  @Action(GET_POLICY_ENQUIRY_LIST)
  public getPolicyEnqueryList(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_POLICY_ENQUIRY_LIST,
  ) {
    return this.dashboardService.getEnquiryList(payload).pipe(
      map((res) => {
        if (res.data) {
          patchState({
            policyEnquiryList: res.data.htgePolicyList,
          });
        } else {
          patchState({
            policyEnquiryList: [],
          });
        }
      }),
    );
  }

  @Action(PAGER_SELECTION_SETTER)
  public pageSelectionSetter(
    { patchState, getState }: StateContext<DashboardModel>,
    { payload }: PAGER_SELECTION_SETTER,
  ) {
    const state = getState();

    patchState({
      ...state,
      pagerSetting: payload,
    });
  }

  @Action(MAIN_PRODUCT_TYPE)
  public mainProductType(
    { patchState, getState }: StateContext<DashboardModel>,
    { payload }: MAIN_PRODUCT_TYPE,
  ) {
    const state = getState();

    patchState({
      ...state,
      mainProductType: payload,
    });
  }

  @Action(PROMPT_ERROR)
  public promptError(
    { patchState }: StateContext<DashboardModel>,
    { payload }: MAIN_PRODUCT_TYPE,
  ) {
    let msg: string = null;
    switch (payload) {
      case 0:
        msg = "Please check your connection and try again.";
        break;
      case 500:
        msg = "Internal server error. Please try again later.";
        break;
      case 504:
        msg = "Request timeout. Please try again later.";
        break;
      default:
        break;
    }
    patchState({
      promptError: {
        isShow: true,
        msg: msg,
      },
    });
  }

  @Action(RESET_PROMPT_ERROR)
  public resetPromptError(
    { patchState, getState: _getState }: StateContext<DashboardModel>,
    { payload: _payload }: MAIN_PRODUCT_TYPE,
  ) {
    patchState({
      promptError: {
        isShow: false,
        msg: null,
      },
    });
  }

  @Action(GET_DOCUMENT_LOV)
  public getDocumentLov(
    { getState, patchState }: StateContext<DashboardModel>,
    { payload }: GET_DOCUMENT_LOV,
  ) {
    const state = getState();
    return this.dashboardService.getDocumentLov(payload).pipe(
      map((res) => {
        if (!res) {
          return;
        }

        const data = res.data ?? {};
        patchState({
          LOV: {
            ...state.LOV,
            documentList: {
              mandatoryViewDocumentList:
                data.mandatoryViewDocumentHashMap ?? [],
              mandatoryUploadDocumentList:
                data.mandatoryUploadDocumentHashMap ?? [],
              optionalViewDocumentList: data.optionalViewDocumentHashMap ?? [],
              optionalUploadDocumentList:
                data.optionalUploadDocumentHashMap ?? [],
              fileMaxSize: data.fileMaxSize ?? null,
              fileTypeList: data.fileTypeList ?? [],
              enhancementCutOffDate: data.enhancementCutOffDate ?? null,
            },
          },
        });
      }),
    );
  }

  @Action(STORE_DASHBOARD_TAB)
  public storeDashboardTab(
    { patchState, getState }: StateContext<DashboardModel>,
    { payload }: any,
  ) {
    const state = getState();
    const tab = payload?.tab;
    const allowedTabs = ["summary", "draft", "expiry", "approval", "motor"];

    if (!allowedTabs.includes(tab)) {
      return;
    }

    const currentTab = state.dashboardTab || {};
    const nextDashboardTab: any = {
      draft: currentTab.draft || null,
      approval: currentTab.approval || null,
      summary: currentTab.summary || null,
      expiry: currentTab.expiry || null,
      motor: currentTab.motor || null,
      [tab]: {
        ...payload.table,
        productType: payload.productType,
      },
    };

    if (tab !== "summary") {
      nextDashboardTab.productType = payload.productType;
    }

    patchState({
      dashboardTab: nextDashboardTab,
    });
  }

  @Action(CLEAR_DASHBOARD_TAB)
  public clearDashboardTab(
    { patchState }: StateContext<DashboardModel>,
    { payload: _payload }: any,
  ) {
    patchState({
      dashboardTab: null,
    });
  }

  @Action(GET_REDIRECT_PAGE)
  public getRedirectPage(
    {
      patchState: _patchState,
      dispatch: _dispatch,
    }: StateContext<DashboardModel>,
    { payload }: GET_REDIRECT_PAGE,
  ) {
    return this.dashboardService.getRedirectPage(payload).pipe(
      map((res) => {
        if (res) {
          if (payload.isRTP) {
            //AGIC-51236
            let newURL = res.url;
            newURL += `&isRTP=${payload.isRTP}`;
            window.open(newURL, "_self");
          } else {
            window.open(res.url, "_self");
          }
        }
      }),
    );
  }

  @Action(REDIRECT_TO_SAT_MOTOR)
  public redirectToSatMotor(
    {
      patchState: _patchState,
      dispatch: _dispatch,
    }: StateContext<DashboardModel>,
    { payload }: GET_REDIRECT_PAGE,
  ) {
    return this.dashboardService.redirectToSatMotor(payload).pipe(
      map((res) => {
        if (!res) {
          return;
        }

        let newURL = res.url;
        //newURL = `http://localhost:4200/sat-motor/home?mock=true&token=${payload.operator}&productCode=${payload.productCode}&contractId=${payload.contractId}&action=${payload.action}&productType=MT`; //for local testing

        if (payload.isMotorApproval) {
          newURL += this.appendParam(
            "isFromMotorApproval",
            payload.isMotorApproval,
          );

          if (payload.searchCriteria) {
            const sc = payload.searchCriteria;
            newURL += this.appendParam("criteria", sc.criteria);
            newURL += this.appendParam("criteriaValue", sc.criteriaValue);
            newURL += this.appendParam("status", sc.status);
            newURL += this.appendParam("cnoteType", sc.cnoteType);
            newURL += this.appendParam("startDate", sc.startDate);
            newURL += this.appendParam("endDate", sc.endDate);
            newURL += this.appendParam("agentCode", sc.agentCode);
            newURL += this.appendParam("branchCode", sc.branchCode);
            newURL += this.appendParam("referredItem", sc.referredItem);
            newURL += this.appendParam("coverNoteNo", sc.coverNoteNo);
            newURL += this.appendParam("vehicleNo", sc.vehicleNo);
            newURL += this.appendParam("marketerManager", sc.marketerManager);
          }
        }

        if (payload.isRTP) {
          //AGIC-51236
          newURL += this.appendParam("isRTP", payload.isRTP);
        }

        window.open(newURL, "_self");
      }),
    );
  }

  private appendParam(key: string, value: string): string {
    if (!value) {
      return "";
    }
    return `&${key}=${value}`;
  }

  @Action(GET_EQUOTE_PRODUCT_ACCESS)
  public hasAccessToEquote(
    { patchState }: StateContext<DashboardModel>,
    { payload }: GET_EQUOTE_PRODUCT_ACCESS,
  ) {
    const requestPayload = {
      userId: payload.userId,
      productType: payload.productType,
    };
    return this.dashboardService.getProductAccess(requestPayload).pipe(
      map((res) => {
        if (res) {
          patchState({
            equoteProductAccess: res.success,
          });
        }
      }),
    );
  }

  @Action(GET_DOCUMENTS)
  public getDocuments(
    { getState, patchState }: StateContext<DashboardModel>,
    { payload }: GET_DOCUMENTS,
  ) {
    const state = getState();
    return this.dashboardService.getDocuments(payload).pipe(
      map((res) => {
        if (res != "{}" && res.data) {
          const combinedDocLovList = this.getCombinedDocumentLovList(
            state,
            payload.productCode,
          );

          const temp = [];

          res.data.forEach((element) => {
            const docData = combinedDocLovList.find(
              (doc) => doc.code == element.code,
            );
            if (docData) {
              element = {
                ...element,
                fileName: element ? element.name + "." + element.type : "",
                descp: docData.descp,
                mandatoryInd: docData.mandatoryInd,
              };
              temp.push(element);
            }
          });

          res.data = temp;

          // uniqueContractId.forEach(id => {
          // for (let i = 0; i < res.data.length; i++) {
          //     const docData = combinedDocLovList.find(doc => doc.code == res.data[i].code);
          //     if (docData && res.data[i].contractId === id) {
          //         const temp = {
          //             ...res.data[i],
          //             fileName: res.data[i].name + '.' + res.data[i].type,
          //             descp: docData.descp,
          //             mandatoryInd: docData.mandatoryInd
          //         }
          //         response.find(val => val.contractId === id).data.push(temp);
          //     }
          // }
          // });
          // }

          patchState({
            cartDocumentDetails: res.data,
          });
        } else {
          console.warn(
            "Document list is empty for " +
              (payload.key == "equoteNo" ? "E-Quotation No" : "Contract Id") +
              "(" +
              payload.value +
              ").",
          );

          patchState({
            cartDocumentDetails: [],
          });
        }
      }),
    );
  }

  @Action(GET_REQUOTE_DOCUMENTS)
  public getRequoteDocuments(
    { getState, patchState }: StateContext<DashboardModel>,
    { payload }: GET_REQUOTE_DOCUMENTS,
  ) {
    const state = getState();
    return this.dashboardService.getRequoteDocuments(payload).pipe(
      map((res) => {
        if (res != "{}" && res.data) {
          const combinedDocLovList = this.getCombinedDocumentLovList(
            state,
            payload.productCode,
          );

          const uniqueContractId = [];
          const response = [];
          res.data.forEach((val) => {
            if (!uniqueContractId.includes(val.contractId)) {
              uniqueContractId.push(val.contractId);

              const temp = {
                contractId: val.contractId,
                data: [],
              };
              response.push(temp);
            }
          });

          uniqueContractId.forEach((id) => {
            for (let i = 0; i < res.data.length; i++) {
              const docData = combinedDocLovList.find(
                (doc) => doc.code == res.data[i].code,
              );
              if (docData && res.data[i].contractId === id) {
                const temp = {
                  ...res.data[i],
                  fileName: res.data[i].name + "." + res.data[i].type,
                  descp: docData.descp,
                  mandatoryInd: docData.mandatoryInd,
                };
                response.find((val) => val.contractId === id).data.push(temp);
              }
            }
          });

          patchState({
            cartDocumentDetails: response,
          });
        } else {
          console.warn(
            "Document list is empty for " +
              (payload.key == "equoteNo" ? "E-Quotation No" : "Contract Id") +
              "(" +
              payload.value +
              ").",
          );

          patchState({
            cartDocumentDetails: [],
          });
        }
      }),
    );
  }

  @Action(DOWNLOAD_DOCUMENT)
  public downloadDocument(
    {
      getState: _getState,
      patchState: _patchState,
    }: StateContext<DashboardModel>,
    { payload }: DOWNLOAD_DOCUMENT,
  ) {
    return this.dashboardService.downloadDocument(payload.docId).pipe(
      map((res: HttpResponse<Blob>) => {
        if (res) {
          if (res.body.size == 0) {
            this.notificationService.openNotificationDialog(
              "The file is not ready yet. Please try again later.",
            );
          } else {
            const file = new Blob([res.body]);
            const a = document.createElement("a");
            const fileUrl = URL.createObjectURL(file);
            a.download = payload.fileName;
            a.href = fileUrl;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
        } else {
          this.notificationService.openNotificationDialog(
            "Failed to download document.",
          );
        }
      }),
    );
  }

  @Action(DOWNLOAD_MOTOR_APPROVAL_REPORT)
  public downloadMotorApprovalReport(
    {
      patchState: _patchState,
      getState: _getState,
    }: StateContext<DashboardModel>,
    { payload }: DOWNLOAD_MOTOR_APPROVAL_REPORT,
  ) {
    return this.dashboardService.downloadMotorApprovalReport(payload);
  }

  @Action(RESET_DASHBOARD_SEARCH_RESULT_LIST)
  public resetSearchResultList({ patchState }: StateContext<DashboardModel>) {
    patchState({
      summaryList: [],
      draftList: [],
      expiringList: [],
      pendingApprovalList: [],
      motorApprovalList: [],
      summaryMTList: [],
      pendingApprovalCount: 0,
      expiringCount: 0,
      summaryCount: 0,
      draftCount: 0,
      motorCount: 0,
      summaryMTCount: 0,
    });
  }

  @Action(GET_ACTIVE_AGENTS_LIST)
  public getActiveAgentsList(
    { patchState, dispatch: _dispatch }: StateContext<DashboardModel>,
    { payload }: GET_ACTIVE_AGENTS_LIST,
  ) {
    return this.dashboardService.getActiveAgentsList(payload).pipe(
      map((res) => {
        patchState({
          activeAgentsList: res ? res : [],
        });
      }),
    );
  }

  @Action(GET_UNIFIED_LOV_IN_SUMMARY_DASHBOARD)
  public getUnifiedLOVInSummary(
    { patchState }: StateContext<DashboardModel>,
    { userId }: GET_UNIFIED_LOV_IN_SUMMARY_DASHBOARD,
  ) {
    return this.dashboardService.getUnifiedLOVInSummaryDashboard(userId).pipe(
      map((res: LOV_RESPONSE) => {
        patchState({
          LOV: res ? res.data : null,
        });
      }),
    );
  }

  @Action(SET_ACITVE_SUMMARY)
  public setActiveSummaryTab(
    { patchState }: StateContext<DashboardModel>,
    { activeSummary }: SET_ACITVE_SUMMARY,
  ) {
    patchState({
      activeSummaryTab: activeSummary,
    });
  }

  @Selector()
  public static activeSummaryTab(state: DashboardModel) {
    return state.activeSummaryTab ? state.activeSummaryTab : [];
  }

  @Selector()
  public static summaryMTList(state: DashboardModel) {
    return state.summaryMTList ? state.summaryMTList : [];
  }

  @Selector()
  public static summaryMTCount(state: DashboardModel) {
    return state.summaryMTCount ? state.summaryMTCount : 0;
  }

  private buildDefaultListPatch(
    payload: DASHBOARD_DefaultParams,
    data?: any,
  ): DashboardDefaultListPatch {
    switch (payload.summaryViewType) {
      case ACTIONS.SUMMARY_DRAFT:
        return {
          draftList:
            data?.cngeProposalCount > 0
              ? data.dashboardCngeProposalNoteList
              : [],
          draftCount: data?.cngeProposalCount ?? 0,
        };
      case ACTIONS.SUMMARY_EXPIRINGSOON:
        return {
          expiringList:
            data?.cngeExpiredProposalCount > 0
              ? data.dashboardCngeExpiredProposalList
              : [],
          expiringCount: data?.cngeExpiredProposalCount ?? 0,
        };
      case ACTIONS.SUMMARY_APPROVAL:
        return {
          pendingApprovalList:
            data?.cngeApprovalCount > 0 ? data.dashboardApprovalNoteList : [],
          pendingApprovalCount: data?.cngeApprovalCount ?? 0,
        };
      case ACTIONS.SUMMARY:
      default:
        if (payload.productType === PRODUCTTYPE.MT) {
          return {
            summaryMTList:
              data?.cngeNoteMTCount > 0 ? data.dashboardCngeNoteMTList : [],
            summaryMTCount: data?.cngeNoteMTCount ?? 0,
          };
        }

        return {
          summaryList:
            data?.cngeNoteCount > 0 ? data.dashboardCngeNoteList : [],
          summaryCount: data?.cngeNoteCount ?? 0,
        };
    }
  }
}
